import { useEffect, useState } from 'react'

import { ApiError } from '@/api/client'
import { partnerApi } from '@/api/endpoints'
import { useCouponMutation } from '@/api/queries'
import { LockIcon, SparkIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { FieldShell, TextArea, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { describeError } from '@/lib/errors'
import { date, moneyShort, pct } from '@/lib/format'
import { PLAN_PRICES, commissionFor, split } from '@/lib/pricing'
import { cn } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { Coupon, CouponDuration } from '@/types/api'

const CODE_RE = /^[A-Z0-9-]{4,24}$/

/** Uppercase, and only what a code may contain. */
export const cleanCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 24)

/**
 * Create a coupon, or edit one. A draft can change everything; once a coupon
 * has been activated its discount and duration are locked (Stripe coupons
 * cannot be edited), so only the note, the use limit and the expiry remain.
 */
export function CouponDialog({
  open,
  onClose,
  coupon,
  maxPct,
  tag,
  fallbackWord,
}: {
  open: boolean
  onClose: () => void
  /** Absent: create. */
  coupon?: Coupon | null
  maxPct: number
  tag: string
  fallbackWord: string
}) {
  const editing = Boolean(coupon)
  const locked = Boolean(coupon && coupon.status !== 'draft')

  const [code, setCode] = useState('')
  const [discount, setDiscount] = useState(0)
  const [duration, setDuration] = useState<CouponDuration>('once')
  const [maxUses, setMaxUses] = useState('')
  const [expires, setExpires] = useState('')
  const [note, setNote] = useState('')
  const [activate, setActivate] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [suggesting, setSuggesting] = useState(false)

  useEffect(() => {
    if (!open) return
    setCode(coupon?.code ?? '')
    setDiscount(coupon ? coupon.discountPct : clamp(Math.round(maxPct / 2 / 0.5) * 0.5, maxPct))
    setDuration(coupon?.duration ?? 'once')
    setMaxUses(coupon?.maxRedemptions != null ? String(coupon.maxRedemptions) : '')
    setExpires(coupon?.expiresAt ? coupon.expiresAt.slice(0, 10) : '')
    setNote(coupon?.note ?? '')
    setActivate(true)
    setErrors({})
  }, [open, coupon, maxPct])

  const onSaved = () => onClose()
  const create = useCouponMutation(partnerApi.createCoupon, { success: (c) => (c && c.status === 'active' ? `${c.code} is live` : `${c ? c.code : 'Coupon'} saved as a draft`) })
  const update = useCouponMutation((args: { id: string; body: Parameters<typeof partnerApi.updateCoupon>[1] }) => partnerApi.updateCoupon(args.id, args.body), { success: () => 'Coupon saved' })
  const busy = create.isPending || update.isPending

  const commission = commissionFor(maxPct, discount)

  const suggest = async () => {
    const word = cleanCode(code.replace(new RegExp(`-?${tag}$`), '')).replace(/-+$/, '') || fallbackWord
    setSuggesting(true)
    try {
      const { code: suggested } = await partnerApi.suggestCode(word)
      setCode(suggested)
      setErrors((e) => ({ ...e, code: '' }))
    } catch (error) {
      const { title, description } = describeError(error, 'Could not suggest a code')
      toast.error(title, description)
    } finally {
      setSuggesting(false)
    }
  }

  const validate = (): boolean => {
    const next: Record<string, string> = {}
    if (!editing && code && !CODE_RE.test(code)) next.code = 'Use 4 to 24 characters: A–Z, 0–9 and -.'
    if (!locked) {
      if (discount < 0 || discount > maxPct) next.discountPct = `Pick 0% to ${pct(maxPct)}.`
      else if (Math.round(discount * 2) !== discount * 2) next.discountPct = 'Use steps of 0.5%.'
    }
    if (maxUses && (!/^\d+$/.test(maxUses) || Number(maxUses) < 1)) next.maxRedemptions = 'A whole number, 1 or more. Leave blank for no limit.'
    if (expires && new Date(`${expires}T23:59:59`).getTime() < Date.now()) next.expiresAt = 'Pick a date in the future.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) return
    const expiresAt = expires ? new Date(`${expires}T23:59:59`).toISOString() : null
    const maxRedemptions = maxUses ? Number(maxUses) : null
    const onError = (error: unknown) => {
      if (error instanceof ApiError) {
        if (error.code === 'coupon_code_taken') setErrors({ code: 'Taken. Try another word, or use Suggest.' })
        else if (error.code === 'discount_above_max') setErrors({ discountPct: `Your maximum is ${pct(maxPct)}.` })
        else if (Object.keys(error.fieldErrors).length) setErrors(error.fieldErrors)
      }
    }
    if (coupon) {
      const body = locked ? { note, maxRedemptions, expiresAt } : { note, maxRedemptions, expiresAt, discountPct: discount, duration }
      update.mutate({ id: coupon.id, body }, { onSuccess: onSaved, onError })
    } else {
      create.mutate(
        { code: code || undefined, discountPct: discount, duration, maxRedemptions, expiresAt, note: note || undefined, activate },
        { onSuccess: onSaved, onError },
      )
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!busy}
      size="lg"
      title={editing ? `Edit ${coupon?.code}` : 'Create coupon'}
      description={editing ? (locked ? 'This code is live, so its discount and duration are fixed.' : 'A draft: change anything until you activate it.') : 'Customers enter the code at checkout, or open your share link.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="coupon-form" loading={busy}>
            {editing ? 'Save' : activate ? 'Create and activate' : 'Save as draft'}
          </Button>
        </>
      }
    >
      <form id="coupon-form" onSubmit={submit} noValidate className="grid gap-5 pb-4 md:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] md:gap-6">
        <div className="grid content-start gap-5">
          {/* ---- Code ---- */}
          {editing ? (
            <FieldShell label="Code">
              <p className="num flex h-11 items-center gap-2 rounded-field border border-line bg-surface-muted px-3.5 text-[15px] font-semibold text-ink">
                {coupon?.code}
                <LockIcon className="h-3.5 w-3.5 text-ink-faint" />
              </p>
            </FieldShell>
          ) : (
            <div>
              <TextField
                appearance="box"
                label="Code"
                className="num pr-[112px] uppercase"
                value={code}
                placeholder={`IPHONE-${tag || 'P1'}`}
                onChange={(event) => setCode(cleanCode(event.target.value))}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                maxLength={24}
                error={errors.code || null}
                trailing={
                  <Button variant="ghost" size="xs" className="mr-1.5" onClick={() => void suggest()} loading={suggesting} icon={<SparkIcon className="h-3.5 w-3.5" />}>
                    Suggest
                  </Button>
                }
              />
              {!errors.code && (
                <p className="mt-1.5 text-[12.5px] text-ink-muted">
                  4–24 characters: A–Z, 0–9 and -. Type a word and press Suggest for a free code ending in -{tag || 'P1'}, or leave it blank and we pick one.
                </p>
              )}
            </div>
          )}

          {/* ---- Discount ---- */}
          <FieldShell label="Customer discount" error={errors.discountPct || null}>
            {locked ? (
              <LockedValue value={`${pct(coupon!.discountPct)} off · you earn ${pct(coupon!.commissionPct)}`} />
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={maxPct}
                  step={0.5}
                  value={discount}
                  onChange={(event) => setDiscount(Number(event.target.value))}
                  aria-label="Customer discount"
                  className="h-2 min-w-0 flex-1 cursor-pointer accent-[rgb(var(--brand))]"
                />
                <div className="relative w-[92px] shrink-0">
                  <input
                    type="number"
                    min={0}
                    max={maxPct}
                    step={0.5}
                    inputMode="decimal"
                    value={Number.isFinite(discount) ? discount : 0}
                    onChange={(event) => setDiscount(clamp(Number(event.target.value) || 0, maxPct))}
                    onBlur={() => setDiscount((d) => clamp(Math.round(d * 2) / 2, maxPct))}
                    aria-label="Customer discount, percent"
                    className="field-box num pr-7 text-right"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] text-ink-muted">%</span>
                </div>
              </div>
            )}
            {!locked && (
              <div className="mt-1 flex justify-between text-[11.5px] text-ink-faint">
                <span>0% · you keep it all</span>
                <span>{pct(maxPct)} · all to the customer</span>
              </div>
            )}
            {/* Phones: the full explainer sits below the form, so keep the split in view next to the slider. */}
            {!locked && (
              <p className="mt-2 rounded-field bg-brand-soft/50 px-3 py-2 text-[12.5px] leading-relaxed text-ink-soft md:hidden" aria-hidden="true">
                Customer gets <span className="font-semibold text-ink">{pct(discount)} off</span> · You earn <span className="font-semibold text-brand">{pct(commission)}</span> · e.g. Pro $69 → pays{' '}
                <span className="num font-semibold text-ink">{moneyShort(split(6900, discount, commission).paidCents)}</span>, you earn{' '}
                <span className="num font-semibold text-brand">{moneyShort(split(6900, discount, commission).commissionCents)}</span>
              </p>
            )}
          </FieldShell>

          {/* ---- Duration ---- */}
          <FieldShell label="Discount applies to">
            {locked ? (
              <LockedValue value={coupon!.duration === 'forever' ? 'Every payment' : 'First payment only'} />
            ) : (
              <div role="radiogroup" aria-label="Discount applies to" className="grid gap-2 xs:grid-cols-2">
                {(
                  [
                    { value: 'once', label: 'First payment only', hint: 'You earn on the first payment.' },
                    { value: 'forever', label: 'Every payment', hint: 'You earn on every renewal too.' },
                  ] as const
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={duration === option.value}
                    onClick={() => setDuration(option.value)}
                    className={cn(
                      'rounded-field border px-3.5 py-2.5 text-left transition',
                      duration === option.value ? 'border-brand/60 bg-brand-soft/50 ring-2 ring-brand/15' : 'border-line hover:border-line-strong',
                    )}
                  >
                    <span className="block text-[14px] font-medium text-ink">{option.label}</span>
                    <span className="block text-[12px] text-ink-muted">{option.hint}</span>
                  </button>
                ))}
              </div>
            )}
          </FieldShell>

          {locked && (
            <p className="flex items-start gap-2 rounded-field bg-surface-muted px-3.5 py-2.5 text-[13px] text-ink-soft">
              <LockIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
              To change the discount, create a new code.
            </p>
          )}

          <div className="grid gap-4 xs:grid-cols-2">
            <TextField
              appearance="box"
              label="Max uses"
              inputMode="numeric"
              placeholder="No limit"
              value={maxUses}
              onChange={(event) => setMaxUses(event.target.value.replace(/[^\d]/g, ''))}
              error={errors.maxRedemptions || null}
              hint={coupon ? `${coupon.redemptions} used so far` : 'Optional'}
            />
            <TextField appearance="box" label="Expires" type="date" value={expires} onChange={(event) => setExpires(event.target.value)} error={errors.expiresAt || null} hint={expires ? `End of ${date(`${expires}T12:00:00`)}` : 'Optional'} />
          </div>

          <TextArea appearance="box" label="Note" rows={2} maxLength={200} placeholder="Only you see this, e.g. Instagram story, October" value={note} onChange={(event) => setNote(event.target.value)} hint={`${note.length}/200`} />

          {!editing && (
            <label className="flex cursor-pointer items-start gap-3 rounded-field border border-line px-3.5 py-3">
              <input type="checkbox" checked={activate} onChange={(event) => setActivate(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[rgb(var(--brand))]" />
              <span>
                <span className="block text-[14px] font-medium text-ink">Activate now</span>
                <span className="block text-[12.5px] text-ink-muted">Customers can use it straight away. Leave unticked to keep a draft you can still change.</span>
              </span>
            </label>
          )}
        </div>

        {/* ---- Live explainer ---- */}
        <Explainer discount={locked ? coupon!.discountPct : discount} commission={locked ? coupon!.commissionPct : commission} duration={locked ? coupon!.duration : duration} />
      </form>
    </Modal>
  )
}

function Explainer({ discount, commission, duration }: { discount: number; commission: number; duration: CouponDuration }) {
  const pro = split(6900, discount, commission)
  return (
    <aside className="h-fit rounded-card border border-brand/25 bg-brand-soft/40 p-4 md:sticky md:top-0" aria-live="polite">
      <p className="label-eyebrow text-brand">How this code splits</p>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-field bg-surface px-3 py-2.5">
          <p className="text-[12px] text-ink-muted">Customer gets</p>
          <p className="num text-[22px] font-semibold leading-tight text-ink">{pct(discount)} off</p>
        </div>
        <div className="rounded-field bg-surface px-3 py-2.5">
          <p className="text-[12px] text-ink-muted">You earn</p>
          <p className="num text-[22px] font-semibold leading-tight text-brand">{pct(commission)}</p>
        </div>
      </div>
      <p className="mt-3 text-[13.5px] leading-relaxed text-ink-soft">
        Customer gets {pct(discount)} off · You earn {pct(commission)} · e.g. Pro {moneyShort(pro.listCents)} → customer pays <span className="num font-semibold text-ink">{moneyShort(pro.paidCents)}</span>, you earn{' '}
        <span className="num font-semibold text-brand">{moneyShort(pro.commissionCents)}</span>
      </p>
      <table className="mt-3 w-full text-[12.5px]">
        <thead>
          <tr className="text-ink-muted">
            <th className="py-1 text-left font-medium">Monthly plan</th>
            <th className="py-1 text-right font-medium">They pay</th>
            <th className="py-1 text-right font-medium">You earn</th>
          </tr>
        </thead>
        <tbody className="num">
          {PLAN_PRICES.map((plan) => {
            const s = split(plan.cents, discount, commission)
            return (
              <tr key={plan.key} className="border-t border-brand/15">
                <td className="py-1.5 font-sans text-ink-soft">
                  {plan.label} <span className="text-ink-faint">{moneyShort(plan.cents)}</span>
                </td>
                <td className="py-1.5 text-right text-ink">{moneyShort(s.paidCents)}</td>
                <td className="py-1.5 text-right font-semibold text-brand">{moneyShort(s.commissionCents)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="mt-3 text-[12px] leading-relaxed text-ink-muted">
        {duration === 'forever' ? 'On every payment of the subscription, for as long as it runs.' : 'On the first payment only; renewals are full price and earn nothing.'} Commission is worked out on the list price.
      </p>
    </aside>
  )
}

function LockedValue({ value }: { value: string }) {
  return (
    <p className="flex h-11 items-center justify-between gap-2 rounded-field border border-line bg-surface-muted px-3.5 text-[14.5px] text-ink">
      <span className="truncate">{value}</span>
      <LockIcon className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
    </p>
  )
}

function clamp(value: number, max: number) {
  return Math.min(Math.max(value, 0), max)
}
